/**
 * Image utility functions for handling image URLs and processing
 */

// Get API base URL from environment or fallback to relative path
// We strip the /api suffix if it exists to get the absolute origin for static files
const rawBaseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '';
const API_BASE_URL = rawBaseUrl.endsWith('/api')
  ? rawBaseUrl.substring(0, rawBaseUrl.length - 4)
  : rawBaseUrl;

/**
 * Convert relative image URL to absolute URL
 * @param url - Image URL (relative or absolute)
 * @returns Absolute image URL
 */
export const getAbsoluteImageUrl = (url: any): string => {
  if (!url) return ''

  if (typeof url === 'object') {
    url = url.url || url.original || url.image_url || url.thumbnail || ''
  }

  if (!url || typeof url !== 'string') return ''

  // If already absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  // If blob URL (for local preview), return as is
  if (url.startsWith('blob:')) {
    return url
  }

  // If data URL (base64), return as is
  if (url.startsWith('data:')) {
    return url
  }

  // Convert relative URL to absolute
  const cleanUrl = url.startsWith('/') ? url : `/${url}`
  return `${API_BASE_URL}${cleanUrl}`
}

/**
 * Get image URL with fallback
 * @param url - Primary image URL
 * @param fallback - Fallback URL if primary is not available
 * @returns Image URL or fallback
 */
export const getImageUrlWithFallback = (
  url: string | null | undefined,
  fallback: string = '/placeholder-image.png'
): string => {
  return getAbsoluteImageUrl(url) || fallback
}

/**
 * Validate image file before upload
 * @param file - File to validate
 * @param maxSizeMB - Maximum file size in MB
 * @returns Error message or null if valid
 */
export const validateImageFile = (file: File, maxSizeMB: number = 5): string | null => {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return 'Please select an image file'
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  if (file.size > maxSizeBytes) {
    return `File size must be less than ${maxSizeMB}MB`
  }

  // Check file format
  const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  if (!allowedFormats.includes(file.type)) {
    return 'Only JPG, PNG, and WebP formats are supported'
  }

  return null
}

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Create image preview URL from file
 * @param file - Image file
 * @returns Promise with preview URL
 */
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      resolve(e.target?.result as string)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Get responsive image srcset for different sizes
 * @param sizes - Object with different image sizes
 * @returns srcset string for responsive images
 */
export const getResponsiveSrcSet = (sizes: {
  thumbnail?: { jpeg: string; webp?: string }
  small?: { jpeg: string; webp?: string }
  medium?: { jpeg: string; webp?: string }
  large?: { jpeg: string; webp?: string }
}): string => {
  const srcset: string[] = []

  if (sizes.thumbnail?.jpeg) {
    srcset.push(`${getAbsoluteImageUrl(sizes.thumbnail.jpeg)} 150w`)
  }
  if (sizes.small?.jpeg) {
    srcset.push(`${getAbsoluteImageUrl(sizes.small.jpeg)} 300w`)
  }
  if (sizes.medium?.jpeg) {
    srcset.push(`${getAbsoluteImageUrl(sizes.medium.jpeg)} 600w`)
  }
  if (sizes.large?.jpeg) {
    srcset.push(`${getAbsoluteImageUrl(sizes.large.jpeg)} 1200w`)
  }

  return srcset.join(', ')
}

/**
 * Check if image URL is valid and accessible
 * @param url - Image URL to check
 * @returns Promise with boolean indicating if image is accessible
 */
export const checkImageExists = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' })
    return response.ok
  } catch {
    return false
  }
}
